import {
	Button,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Separator,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	Textarea,
} from "@monbits/ui";
import {
	Activity,
	AlertCircle,
	Ban,
	Building2,
	DollarSign,
	Loader2,
	Pencil,
	Percent,
	Plus,
	Receipt,
	Shield,
	ShieldAlert,
	Trash2,
	X,
} from "lucide-react";
import * as React from "react";
import type {
	MerchantDetails,
	UpsertUserWithdrawalFeeRequest,
	UserDetails,
	UserWithdrawalFee,
} from "src/providers/user-provider";
import type { AuditLog, CurrencySetting } from "src/types";
import { AuditLogTimeline } from "@/components/audit-log-timeline";

interface UserDrawerContentProps {
	user: UserDetails;
	userMerchants: MerchantDetails[];
	isLoadingMerchants: boolean;
	isLoadingSettings: boolean;
	serviceFeeRate: string;
	maxMerchants: string;
	blockReason: string;
	isBlocking: boolean;
	auditLogs: AuditLog[];
	isLoadingAudit: boolean;
	onServiceFeeRateChange: (value: string) => void;
	onMaxMerchantsChange: (value: string) => void;
	onBlockReasonChange: (value: string) => void;
	onBlockUser: () => void;
	onUnblockUser: () => void;
	formatDate: (date: string) => string;
	withdrawalFees: UserWithdrawalFee[];
	isLoadingWithdrawalFees: boolean;
	isSavingWithdrawalFee: boolean;
	availableCurrencies: CurrencySetting[];
	onSaveWithdrawalFee: (fee: UpsertUserWithdrawalFeeRequest) => Promise<void>;
	onDeleteWithdrawalFee: (ticker: string, feeProfile?: string | null) => void;
}

export const UserDrawerContent: React.FC<UserDrawerContentProps> = ({
	user,
	userMerchants,
	isLoadingMerchants,
	isLoadingSettings,
	serviceFeeRate,
	maxMerchants,
	blockReason,
	isBlocking,
	auditLogs,
	isLoadingAudit,
	onServiceFeeRateChange,
	onMaxMerchantsChange,
	onBlockReasonChange,
	onBlockUser,
	onUnblockUser,
	formatDate,
	withdrawalFees,
	isLoadingWithdrawalFees,
	isSavingWithdrawalFee,
	availableCurrencies,
	onSaveWithdrawalFee,
	onDeleteWithdrawalFee,
}) => {
	const [editingFee, setEditingFee] = React.useState<UserWithdrawalFee | null>(
		null,
	);
	const [isAddingFee, setIsAddingFee] = React.useState(false);
	const [confirmingDelete, setConfirmingDelete] = React.useState<string | null>(
		null,
	);

	const feeKey = (fee: UserWithdrawalFee) =>
		`${fee.ticker}:${fee.feeProfile ?? "all"}`;

	return (
		<>
			{/* Blocked Status Alert */}
			{user.blockedAt && (
				<div className="flex items-start gap-3 p-4 mx-6 mt-6 border rounded-lg bg-muted/50">
					<ShieldAlert className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
					<div className="flex-1 space-y-1">
						<div className="font-medium text-sm">Account Blocked</div>
						<div className="text-sm text-muted-foreground">
							{user.blockedReason || "No reason provided"}
						</div>
						<div className="text-xs text-muted-foreground">
							{formatDate(user.blockedAt)}
						</div>
					</div>
				</div>
			)}

			<Tabs defaultValue="details" className="w-full">
				<TabsList className="mx-6 mt-6 w-[calc(100%-3rem)] grid grid-cols-3">
					<TabsTrigger value="details">Details</TabsTrigger>
					<TabsTrigger value="activity">Activity</TabsTrigger>
					<TabsTrigger value="actions">Actions</TabsTrigger>
				</TabsList>

				<TabsContent value="details" className="px-6 pb-6 space-y-6 mt-6">
					{/* User Details Section */}
					<div className="space-y-4">
						<h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
							Details
						</h3>
						<div className="space-y-3">
							<div>
								<div className="text-xs font-medium text-muted-foreground mb-1">
									UUID
								</div>
								<div className="font-mono text-sm break-all">{user.uuid}</div>
							</div>
							<div>
								<div className="text-xs font-medium text-muted-foreground mb-1">
									Name
								</div>
								<div className="text-sm">{user.name}</div>
							</div>
							<div>
								<div className="text-xs font-medium text-muted-foreground mb-1">
									Email
								</div>
								<div className="text-sm">{user.email}</div>
							</div>
							<div>
								<div className="text-xs font-medium text-muted-foreground mb-1">
									Created At
								</div>
								<div className="text-sm">{formatDate(user.createdAt)}</div>
							</div>
							{user.profileImageUrl && (
								<div>
									<div className="text-xs font-medium text-muted-foreground mb-1">
										Profile Image
									</div>
									<img
										src={user.profileImageUrl}
										alt={user.name}
										className="h-16 w-16 rounded-full border"
									/>
								</div>
							)}
						</div>
					</div>

					<Separator />

					{/* User Merchants Section */}
					<div className="space-y-4">
						<h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
							Merchants ({user.merchantCount})
						</h3>

						{isLoadingMerchants ? (
							<div className="flex justify-center py-8">
								<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
							</div>
						) : userMerchants.length === 0 ? (
							<div className="flex flex-col items-center justify-center py-8 px-4 text-center border rounded-lg bg-muted/50">
								<Building2 className="h-8 w-8 text-muted-foreground mb-2" />
								<p className="text-sm text-muted-foreground">
									No merchants yet
								</p>
							</div>
						) : (
							<div className="space-y-2">
								{userMerchants.map((merchant) => (
									<div
										key={merchant.uuid}
										className="p-3 border rounded-lg hover:bg-muted/50 transition-colors"
									>
										<div className="flex items-start justify-between">
											<div className="flex-1">
												<div className="font-medium text-sm">
													{merchant.name}
												</div>
												{merchant.website && (
													<a
														href={merchant.website}
														target="_blank"
														rel="noopener noreferrer"
														className="text-xs text-blue-600 hover:underline"
													>
														{merchant.website}
													</a>
												)}
												<div className="text-xs text-muted-foreground mt-1">
													Created {formatDate(merchant.createdAt)}
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						)}
					</div>

					<Separator />

					{/* User Settings Section */}
					<div className="space-y-4">
						<h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
							Settings
						</h3>

						{isLoadingSettings ? (
							<div className="flex justify-center py-8">
								<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
							</div>
						) : (
							<div className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="serviceFeeRate" className="text-sm">
										Service Fee Rate
									</Label>
									<Input
										id="serviceFeeRate"
										type="number"
										step="0.01"
										min="0"
										max="1"
										placeholder="0.05"
										value={serviceFeeRate}
										onChange={(e) => onServiceFeeRateChange(e.target.value)}
									/>
									<p className="text-xs text-muted-foreground">
										Decimal value (e.g., 0.05 for 5%)
									</p>
								</div>

								<div className="space-y-2">
									<Label htmlFor="maxMerchants" className="text-sm">
										Max Merchants
									</Label>
									<Input
										id="maxMerchants"
										type="number"
										min="-1"
										placeholder="Unlimited"
										value={maxMerchants}
										onChange={(e) => onMaxMerchantsChange(e.target.value)}
									/>
									<p className="text-xs text-muted-foreground">
										-1 = unlimited, 0 = disabled, positive = limit
									</p>
								</div>
							</div>
						)}
					</div>

					<Separator />

					<div className="space-y-4">
						<div className="flex items-center justify-between">
							<h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
								Withdrawal Fee Overrides
							</h3>
							{!isLoadingWithdrawalFees && !editingFee && !isAddingFee && (
								<Button
									variant="ghost"
									size="sm"
									className="h-7 px-2 text-xs"
									onClick={() => setIsAddingFee(true)}
								>
									<Plus className="h-3.5 w-3.5 mr-1" />
									Add
								</Button>
							)}
						</div>

						{isLoadingWithdrawalFees ? (
							<div className="flex justify-center py-8">
								<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
							</div>
						) : (
							<>
								{withdrawalFees.length > 0 ? (
									<div className="space-y-1.5">
										{withdrawalFees.map((fee) => {
											const currency = availableCurrencies.find(
												(c) => c.ticker === fee.ticker,
											);
											const isConfirming = confirmingDelete === feeKey(fee);
											return (
												<div
													key={feeKey(fee)}
													className="group relative flex items-center gap-3 px-3 py-2.5 border rounded-lg transition-colors hover:bg-muted/40"
												>
													<div className="flex-1 min-w-0">
														<div className="flex items-center gap-2 mb-0.5">
															<span className="text-sm font-medium truncate">
																{currency?.displayName ?? fee.ticker}
															</span>
															<span className="text-[10px] text-muted-foreground">
																{currency?.blockchainName}
															</span>
															<FeeProfileBadge profile={fee.feeProfile} />
														</div>
														<div className="flex items-center gap-3 text-xs">
															{fee.fixedUsd != null ? (
																<span className="font-mono tabular-nums">
																	<span className="text-muted-foreground">
																		$
																	</span>
																	{fee.fixedUsd.toFixed(2)}
																	<span className="text-muted-foreground ml-1">
																		fixed
																	</span>
																</span>
															) : (
																<span className="text-muted-foreground/60">
																	No fixed fee
																</span>
															)}
															<span className="text-muted-foreground/30">
																+
															</span>
															{fee.percentage != null ? (
																<span className="font-mono tabular-nums">
																	{(fee.percentage * 100)
																		.toFixed(4)
																		.replace(/\.?0+$/, "")}
																	<span className="text-muted-foreground">
																		%
																	</span>
																</span>
															) : (
																<span className="text-muted-foreground/60">
																	No rate
																</span>
															)}
														</div>
													</div>
													{isConfirming ? (
														<div className="flex items-center gap-1.5">
															<Button
																variant="destructive"
																size="sm"
																className="h-7 px-2 text-xs"
																onClick={() => {
																	onDeleteWithdrawalFee(
																		fee.ticker,
																		fee.feeProfile,
																	);
																	setConfirmingDelete(null);
																}}
															>
																Remove
															</Button>
															<Button
																variant="ghost"
																size="sm"
																className="h-7 px-2 text-xs"
																onClick={() => setConfirmingDelete(null)}
															>
																Cancel
															</Button>
														</div>
													) : (
														<div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
															<Button
																variant="ghost"
																size="icon"
																className="h-7 w-7"
																onClick={() => setEditingFee(fee)}
															>
																<Pencil className="h-3.5 w-3.5" />
															</Button>
															<Button
																variant="ghost"
																size="icon"
																className="h-7 w-7"
																onClick={() => setConfirmingDelete(feeKey(fee))}
															>
																<Trash2 className="h-3.5 w-3.5" />
															</Button>
														</div>
													)}
												</div>
											);
										})}
									</div>
								) : (
									<div className="flex flex-col items-center justify-center py-10 px-4 text-center border border-dashed rounded-lg">
										<Receipt className="h-6 w-6 text-muted-foreground/50 mb-2" />
										<p className="text-sm text-muted-foreground">
											No custom fees configured
										</p>
										<p className="text-xs text-muted-foreground/60 mt-0.5">
											Global currency defaults apply to all withdrawals
										</p>
									</div>
								)}

								{(editingFee || isAddingFee) && (
									<WithdrawalFeeForm
										onSave={async (fee) => {
											await onSaveWithdrawalFee(fee);
											setIsAddingFee(false);
										}}
										isSaving={isSavingWithdrawalFee}
										existingTickers={withdrawalFees.map((f) => f.ticker)}
										availableCurrencies={availableCurrencies}
										editingFee={editingFee}
										onCancelEdit={() => {
											setEditingFee(null);
											setIsAddingFee(false);
										}}
									/>
								)}
							</>
						)}
					</div>
				</TabsContent>

				<TabsContent value="activity" className="px-6 pb-6 mt-6">
					<div className="space-y-4">
						<h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
							<Activity className="h-4 w-4" />
							Recent Activity
						</h3>
						<AuditLogTimeline logs={auditLogs} isLoading={isLoadingAudit} />
					</div>
				</TabsContent>

				<TabsContent value="actions" className="px-6 pb-6 mt-6">
					<div className="space-y-4">
						<h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
							Danger Zone
						</h3>

						{user.blockedAt ? (
							<div className="space-y-3">
								<div className="text-sm text-muted-foreground">
									This account is currently blocked and cannot access the
									system. You can restore access by unblocking the user.
								</div>
								<Button
									onClick={onUnblockUser}
									disabled={isBlocking}
									variant="outline"
									size="sm"
								>
									{isBlocking ? (
										<>
											<Loader2 className="h-3 w-3 mr-2 animate-spin" />
											Unblocking...
										</>
									) : (
										<>
											<Shield className="h-3 w-3 mr-2" />
											Unblock User
										</>
									)}
								</Button>
							</div>
						) : (
							<div className="space-y-3">
								<div className="flex items-start gap-2 p-3 border rounded-lg bg-muted/30">
									<AlertCircle className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
									<div className="text-xs text-muted-foreground">
										Blocking a user will immediately prevent them from accessing
										their account and all associated services.
									</div>
								</div>

								<div className="space-y-2">
									<Label htmlFor="blockReason" className="text-sm">
										Reason for Blocking
									</Label>
									<Textarea
										id="blockReason"
										placeholder="Provide a clear reason..."
										value={blockReason}
										onChange={(e) => onBlockReasonChange(e.target.value)}
										rows={3}
										className="text-sm"
									/>
								</div>

								<Button
									onClick={onBlockUser}
									disabled={isBlocking || !blockReason.trim()}
									variant="destructive"
									size="sm"
								>
									{isBlocking ? (
										<>
											<Loader2 className="h-3 w-3 mr-2 animate-spin" />
											Blocking...
										</>
									) : (
										<>
											<Ban className="h-3 w-3 mr-2" />
											Block User
										</>
									)}
								</Button>
							</div>
						)}
					</div>
				</TabsContent>
			</Tabs>
		</>
	);
};

function FeeProfileBadge({ profile }: { profile: string | null }) {
	if (profile === "merchant") {
		return (
			<span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400">
				Merchant
			</span>
		);
	}
	if (profile === "client") {
		return (
			<span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400">
				Client
			</span>
		);
	}
	return (
		<span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground">
			All
		</span>
	);
}

function WithdrawalFeeForm({
	onSave,
	isSaving,
	existingTickers,
	availableCurrencies,
	editingFee,
	onCancelEdit,
}: {
	onSave: (fee: UpsertUserWithdrawalFeeRequest) => Promise<void>;
	isSaving: boolean;
	existingTickers: string[];
	availableCurrencies: CurrencySetting[];
	editingFee: UserWithdrawalFee | null;
	onCancelEdit: () => void;
}) {
	const [ticker, setTicker] = React.useState("");
	const [feeProfile, setFeeProfile] = React.useState<string>("all");
	const [fixedUsd, setFixedUsd] = React.useState("");
	const [percentageDisplay, setPercentageDisplay] = React.useState("");
	const [fixedError, setFixedError] = React.useState("");
	const [percentageError, setPercentageError] = React.useState("");

	React.useEffect(() => {
		if (editingFee) {
			setTicker(editingFee.ticker);
			setFeeProfile(editingFee.feeProfile ?? "all");
			setFixedUsd(editingFee.fixedUsd?.toString() ?? "");
			setPercentageDisplay(
				editingFee.percentage != null
					? (editingFee.percentage * 100).toString()
					: "",
			);
			setFixedError("");
			setPercentageError("");
		}
	}, [editingFee]);

	const resetForm = () => {
		setTicker("");
		setFeeProfile("all");
		setFixedUsd("");
		setPercentageDisplay("");
		setFixedError("");
		setPercentageError("");
		onCancelEdit();
	};

	const handleSubmit = async () => {
		if (!ticker) return;

		let hasError = false;
		setFixedError("");
		setPercentageError("");

		const fee: UpsertUserWithdrawalFeeRequest = {
			ticker,
			feeProfile: feeProfile === "all" ? null : feeProfile,
		};
		if (fixedUsd.trim()) {
			const parsed = Number.parseFloat(fixedUsd);
			if (Number.isNaN(parsed) || parsed < 0) {
				setFixedError("Must be 0 or greater");
				hasError = true;
			} else {
				fee.fixedUsd = parsed;
			}
		}
		if (percentageDisplay.trim()) {
			const displayVal = Number.parseFloat(percentageDisplay);
			if (Number.isNaN(displayVal) || displayVal < 0 || displayVal > 50) {
				setPercentageError("Must be between 0 and 50");
				hasError = true;
			} else {
				fee.percentage = displayVal / 100;
			}
		}

		if (hasError) return;

		try {
			await onSave(fee);
			resetForm();
		} catch {
			// Error already handled by parent (toast). Keep form open for retry.
		}
	};

	const selectableCurrencies = editingFee
		? availableCurrencies.filter((c) => c.ticker === editingFee.ticker)
		: availableCurrencies.filter((c) => !existingTickers.includes(c.ticker));

	return (
		<div className="border rounded-lg overflow-hidden">
			<div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b">
				<span className="text-xs font-medium">
					{editingFee ? "Edit Override" : "New Override"}
				</span>
				<Button
					variant="ghost"
					size="icon"
					className="h-6 w-6"
					onClick={resetForm}
					disabled={isSaving}
				>
					<X className="h-3.5 w-3.5" />
				</Button>
			</div>
			<div className="p-3 space-y-3">
				<div className="grid grid-cols-2 gap-3">
					<div className="space-y-1.5">
						<Label
							htmlFor="wf-currency"
							className="text-xs text-muted-foreground"
						>
							Currency
						</Label>
						<Select
							value={ticker}
							onValueChange={setTicker}
							disabled={!!editingFee}
						>
							<SelectTrigger id="wf-currency" className="w-full h-9">
								<SelectValue placeholder="Select..." />
							</SelectTrigger>
							<SelectContent>
								{selectableCurrencies.map((c) => (
									<SelectItem key={c.ticker} value={c.ticker}>
										{c.displayName} ({c.blockchainName})
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-1.5">
						<Label
							htmlFor="wf-fee-profile"
							className="text-xs text-muted-foreground"
						>
							Applies to
						</Label>
						<Select
							value={feeProfile}
							onValueChange={setFeeProfile}
							disabled={!!editingFee}
						>
							<SelectTrigger id="wf-fee-profile" className="w-full h-9">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All withdrawals</SelectItem>
								<SelectItem value="merchant">Merchant only</SelectItem>
								<SelectItem value="client">Client only</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>
				<div className="grid grid-cols-2 gap-3">
					<div className="space-y-1.5">
						<Label
							htmlFor="wf-fixed-usd"
							className="text-xs text-muted-foreground"
						>
							Fixed fee
						</Label>
						<div className="relative">
							<DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/50" />
							<Input
								id="wf-fixed-usd"
								type="number"
								step="0.01"
								min="0"
								placeholder="Global default"
								value={fixedUsd}
								onChange={(e) => {
									setFixedUsd(e.target.value);
									setFixedError("");
								}}
								className="pl-7 h-9 font-mono tabular-nums"
								aria-invalid={!!fixedError}
								aria-describedby={fixedError ? "wf-fixed-error" : undefined}
							/>
						</div>
						{fixedError && (
							<p id="wf-fixed-error" className="text-[11px] text-destructive">
								{fixedError}
							</p>
						)}
					</div>
					<div className="space-y-1.5">
						<Label
							htmlFor="wf-percentage"
							className="text-xs text-muted-foreground"
						>
							Rate
						</Label>
						<div className="relative">
							<Input
								id="wf-percentage"
								type="number"
								step="0.1"
								min="0"
								max="50"
								placeholder="0"
								value={percentageDisplay}
								onChange={(e) => {
									setPercentageDisplay(e.target.value);
									setPercentageError("");
								}}
								className="pr-7 h-9 font-mono tabular-nums"
								aria-invalid={!!percentageError}
								aria-describedby={percentageError ? "wf-pct-error" : undefined}
							/>
							<Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/50" />
						</div>
						{percentageError && (
							<p id="wf-pct-error" className="text-[11px] text-destructive">
								{percentageError}
							</p>
						)}
					</div>
				</div>
				<div className="flex gap-2 pt-1">
					<Button
						size="sm"
						className="h-8"
						onClick={handleSubmit}
						disabled={!ticker || isSaving}
					>
						{isSaving ? (
							<>
								<Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
								Saving...
							</>
						) : editingFee ? (
							"Update"
						) : (
							"Save"
						)}
					</Button>
					<Button
						size="sm"
						variant="ghost"
						className="h-8"
						onClick={resetForm}
						disabled={isSaving}
					>
						Cancel
					</Button>
				</div>
			</div>
		</div>
	);
}
